import { z } from "zod"
import { S3_UPLOAD_INPUT_FORMATS } from "../../shared/files"
import { WhatsappMessageStatusUpdateEnum } from "../../webhooks"
import { ImproveWhatsappTemplateSchema, WhatsappTemplateButtonUrlTypeSchema } from "./dtos"

export const WhatsappTemplateResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  internalId: z.string(),
  name: z.string(),
  isOneTime: z.boolean(),
  header: z.string().nullable(),
  body: z.string(),
  footer: z.string().nullable(),
  language: z.string(),
  whatsappIntegrationId: z.bigint().transform(String),
  status: WhatsappMessageStatusUpdateEnum,
  category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"]),
  whatsappTemplateVariables: z
    .array(
      z.object({
        paramName: z.string(),
        example: z.string(),
        description: z.string().nullish(),
      }),
    )
    .nullish(),
  metrics: z
    .object({
      timesUsed: z.number().nullable(),
      timesReplied: z.number().nullable(),
      engageRate: z.number().nullable(),
    })
    .nullish(),
  mediaUrl: z.string().nullish(),
  mediaFormat: S3_UPLOAD_INPUT_FORMATS.nullish(),
  whatsappTemplateButtons: z
    .array(
      z.object({
        index: z.number(),
        text: z.string(),
        urlType: WhatsappTemplateButtonUrlTypeSchema,
        url: z.string().nullish(),
        variableName: z.string().nullish(),
        variableDescription: z.string().nullish(),
      }),
    )
    .nullish(),
  editedAt: z
    .date()
    .transform((value) => value.toISOString())
    .nullish(),
})

export const WhatsappTemplatesResponseDataSchema = z.array(WhatsappTemplateResponseDataSchema)

export const WhatsappConfigurationRespondeDataSchema = z
  .object({
    accountId: z.string(),
    accessToken: z.string().transform((e) => `${e.substring(0, 100)}...`),
    hosted: z.boolean(),
    // true when the accountId belongs to Permut's shared default account
    // (as opposed to an account onboarded by the organization via embedded signup).
    isPermutAccount: z.boolean(),
  })
  .nullable()

export const WhatsappNumberResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  number: z.string(),
  numberId: z.string(),
  name: z.string(),
  status: z.enum(["CONNECTED", "PENDING"]),
  quality: z.enum(["GREEN", "YELLOW", "RED", "UNKNOWN", "NA"]).nullish(),
  companyId: z.bigint().transform(String).nullable(),
  failedAt: z
    .date()
    .transform((value) => value.toISOString())
    .nullish(),
  failedReason: z.string().nullish(),
  isOnBusinessApp: z.boolean().default(false),
  coexistenceSyncs: z
    .array(
      z.object({
        type: z.string().pipe(z.enum(["smb_app_state_sync", "history"])),
        status: z
          .string()
          .pipe(
            z.enum([
              "pending",
              "requesting",
              "requested",
              "completed",
              "declined",
              "failed",
              "unknown",
            ]),
          ),
        requestId: z.string().nullable(),
        requestedAt: z
          .date()
          .transform((value) => value.toISOString())
          .nullable(),
        error: z.string().nullable(),
        progress: z.number().nullable(),
      }),
    )
    .default([]),
})

export const EmbeddedSignupResponseDataSchema = z.object({
  needsPhoneSelection: z.boolean(),
  numbers: z.array(z.object({ id: z.string(), number: z.string(), name: z.string() })),
})

export const WhatsappNumbersResponseDataSchema = z.array(WhatsappNumberResponseDataSchema)
export const WhatsappPhoneNumberRegisterResponseDataSchema = z.object({ success: z.boolean() })

export const WhatsappAccountAlertResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  accountId: z.string(),
  externalId: z.string(),
  type: z.string(),
  severity: z.string(),
  status: z.string(),
  description: z.string(),
  entityId: z.string(),
  entityType: z.string(),
  occurredAt: z
    .date()
    .transform((value) => value.toISOString())
    .nullable(),
  updatedAt: z.date().transform((value) => value.toISOString()),
})

export const WhatsappAccountAlertsResponseDataSchema = z.array(
  WhatsappAccountAlertResponseDataSchema,
)

// Whatsapp opening messages
export const WhatsappOpeningMessageResponseData = z.object({
  id: z.bigint().transform(String),
  identifier: z.string(),
  locale: z.string(),
  text: z.string(),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  archivedAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  campaign: z
    .object({
      id: z.bigint().transform(String),
      name: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
})
export const WhatsappOpeningMessagesResponseData = z.array(WhatsappOpeningMessageResponseData)

export const TranslateWhatsappTemplateResponseData = z.object({
  translations: z.array(
    z.object({
      language: z.string(),
      body: z.string(),
      buttonTexts: z.array(z.string().min(1)).nullish(),
    }),
  ),
})

export const ImproveWhatsappTemplateResponseData = ImproveWhatsappTemplateSchema

export const WhatsappTemplatesCommonLocalesResponseData = z.array(
  z.object({ locale: z.string(), count: z.number() }),
)

export const CreateTranslatedTemplatesResponseData = z.array(
  z.object({ name: z.string(), id: z.bigint().transform(String) }),
)
export const UploadMediaWhatsappTemplateResponseData = z.object({
  url: z.string(),
})
